import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { PurchaseRequest, RequestStatus, RequestFrequency } from '../../types/request.types';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchRequests, fetchMyRequests, fetchDepartmentRequests } from '../../store/slices/requestSlice';
import { UserRole } from '../../types/auth.types';

interface RequestListProps {
  onViewRequest: (request: PurchaseRequest) => void;
  onEditRequest?: (request: PurchaseRequest) => void;
  title?: string;
  refreshTrigger?: number; // Add this to trigger refresh when value changes
  statusFilter?: string;
  departmentFilter?: string;
  mode?: 'my-requests' | 'pending-approvals';
}

const RequestList: React.FC<RequestListProps> = ({
  onViewRequest,
  onEditRequest,
  title = 'Purchase Requests',
  refreshTrigger,
  statusFilter = 'ALL',
  departmentFilter = 'ALL',
  mode,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { requests, loading, error } = useAppSelector((state) => state.request);
  const user = useAppSelector((state) => state.auth.user);

  const fetchRequestsData = React.useCallback(() => {
    if (!user) return;

    // Handle mode-based data fetching for roles that support separation
    if (mode && (user.role === UserRole.DEPARTMENT_MANAGER || user.role === UserRole.GENERAL_MANAGER || user.role === UserRole.FINANCE_ADMIN)) {
      switch (mode) {
        case 'my-requests':
          // Fetch their own requests (like an employee)
          dispatch(fetchMyRequests());
          break;
        case 'pending-approvals':
          // Fetch requests for approval based on role
          if (user.role === UserRole.DEPARTMENT_MANAGER) {
            dispatch(fetchDepartmentRequests());
          } else {
            dispatch(fetchRequests());
          }
          break;
        default:
          dispatch(fetchRequests());
          break;
      }
      return;
    }

    // Default behavior for backward compatibility
    switch (user.role) {
      case UserRole.EMPLOYEE:
        dispatch(fetchMyRequests());
        break;
      case UserRole.DEPARTMENT_MANAGER:
        dispatch(fetchDepartmentRequests());
        break;
      case UserRole.SENIOR_MANAGER:
      case UserRole.GENERAL_MANAGER:
      case UserRole.FINANCE_ADMIN:
      case UserRole.CFO:
      case UserRole.SYSTEM_ADMIN:
        dispatch(fetchRequests());
        break;
    }
  }, [dispatch, user, mode]);

  useEffect(() => {
    fetchRequestsData();
  }, [fetchRequestsData]);

  // Refresh data when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchRequestsData();
    }
  }, [refreshTrigger, fetchRequestsData]);

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.DRAFT:
        return theme.palette.grey[500];
      case RequestStatus.PENDING_MANAGER:
      case RequestStatus.PENDING_SENIOR_MANAGER:
      case RequestStatus.PENDING_GENERAL_MANAGER:
      case RequestStatus.PENDING_FINANCE:
      case RequestStatus.PENDING_CFO:
        return theme.palette.warning.main;
      case RequestStatus.MORE_INFO_NEEDED:
        return theme.palette.info.main;
      case RequestStatus.APPROVED:
        return theme.palette.success.main;
      case RequestStatus.REJECTED:
        return theme.palette.error.main;
      case RequestStatus.PAID:
      case RequestStatus.CLOSED:
        return theme.palette.secondary.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  // Filter requests based on status and department
  const filteredRequests = React.useMemo(() => {
    let filtered = [...requests];

    // Apply mode-based filtering for managers
    if (mode === 'pending-approvals') {
      switch (user?.role) {
        case UserRole.DEPARTMENT_MANAGER:
          // Only show requests that need the department manager's approval
          filtered = filtered.filter(request => 
            request.status === RequestStatus.PENDING_MANAGER && 
            request.departmentId === user.departmentId
          );
          break;
        case UserRole.GENERAL_MANAGER:
          // Only show requests that need the general manager's approval
          filtered = filtered.filter(request => 
            request.status === RequestStatus.PENDING_GENERAL_MANAGER
          );
          break;
        case UserRole.FINANCE_ADMIN:
          // Only show requests that need finance approval (already approved by GM)
          filtered = filtered.filter(request => 
            request.status === RequestStatus.PENDING_FINANCE
          );
          break;
      }
    }

    // Apply status filter
    if (statusFilter !== 'ALL') {
      if (statusFilter === 'PENDING') {
        // Filter for all pending statuses
        const pendingStatuses = [
          RequestStatus.PENDING_MANAGER,
          RequestStatus.PENDING_SENIOR_MANAGER,
          RequestStatus.PENDING_GENERAL_MANAGER,
          RequestStatus.PENDING_FINANCE,
          RequestStatus.PENDING_CFO,
          RequestStatus.MORE_INFO_NEEDED,
        ];
        filtered = filtered.filter(request => pendingStatuses.includes(request.status));
      } else {
        // Filter for specific status
        filtered = filtered.filter(request => request.status === statusFilter);
      }
    }

    // Apply department filter
    if (departmentFilter !== 'ALL') {
      filtered = filtered.filter(request => request.department.name === departmentFilter);
    }

    return filtered;
  }, [requests, statusFilter, departmentFilter, user, mode]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading requests...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredRequests.length === requests.length 
              ? `${requests.length} requests`
              : `${filteredRequests.length} of ${requests.length} requests`
            }
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Requester</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>
                    <Typography
                      sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        maxWidth: '120px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={request.id}
                    >
                      {request.id}
                    </Typography>
                  </TableCell>
                  <TableCell>{request.title}</TableCell>
                  <TableCell>
                    {request.requester.firstName} {request.requester.lastName}
                  </TableCell>
                  <TableCell>{request.department.name}</TableCell>
                  <TableCell>
                    {formatCurrency(request.amount, request.currency)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.frequency || 'ONCE'}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.75rem',
                        height: '24px'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status.replace(/_/g, ' ')}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(request.status),
                        color: theme.palette.getContrastText(
                          getStatusColor(request.status)
                        ),
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {format(new Date(request.createdAt), 'PP')}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => onViewRequest(request)}
                        title="View Details"
                      >
                        <ViewIcon />
                      </IconButton>
                      {onEditRequest && 
                       request.status === RequestStatus.DRAFT && 
                       (user?.role === UserRole.EMPLOYEE || 
                        (user?.role === UserRole.DEPARTMENT_MANAGER && request.requesterId === user.id)) && (
                        <IconButton
                          size="small"
                          onClick={() => onEditRequest(request)}
                          title="Edit Request"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRequests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {requests.length === 0 ? 'No requests found' : 'No requests match the current filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default RequestList; 